import { ApiError, OfflineError, type WeatherResponse } from '../../data/api'
import {
  weatherHoursFrom,
  weatherRecheckDue,
  weatherSamples,
  weatherState,
  WEATHER_ATTRIBUTION,
} from '../../lib/weather'
import type { DiaryEntry, DiaryWeather, Stage, TrackFix } from '../../lib/types'

/**
 * Der Nachlauf: für jede Etappe mit Aufzeichnung das Wetter holen, eine nach
 * der anderen.
 *
 * Bewusst kein React in dieser Datei. Der Ablauf - wer drankommt, in welcher
 * Reihenfolge, was ein Fehler bedeutet - ist die eigentliche Entscheidung und
 * lässt sich so ohne DOM prüfen. Dieselbe Aufteilung wie bei
 * features/stages/stageRouting.ts.
 *
 * Der Unterschied zum Sammelaufruf der Strecken: dieser hier läuft von selbst
 * los, sobald Netz da ist. Der Grund ist der Ort, an dem er gebraucht wird -
 * eine GPX-Datei wird abends auf dem Campingplatz eingelesen, oft ohne
 * Verbindung. Ein Knopf, den man dann drückt, tut nichts, und am nächsten
 * Morgen denkt niemand mehr daran. „Kein Netz“ ist hier kein Fehler, sondern
 * ein Wartezustand: gemerkt wird, dass für diesen Tag Wetter abrufbar ist, und
 * geholt wird es beim nächsten Mal.
 *
 * Dass er das darf, hängt am Dienst: Open-Meteo kostet nichts, hat keine
 * Abrechnung und erlaubt 10 000 Abrufe am Tag, und der Server legt jede Antwort
 * ab. Eine Reise mit dreissig aufgezeichneten Etappen holt einmal dreissig
 * Werte und danach nur noch dort nach, wo der Dienst am Abend der Fahrt selbst
 * noch nichts hatte - höchstens einmal am Tag und höchstens, bis der Tag beim
 * Dienst fertig ist. Beim Routendienst mit seiner einen Anfrage je Sekunde wäre
 * dieselbe Freiheit unhöflich, und dort bleibt es beim Knopf.
 */

/**
 * Mindestabstand zwischen zwei Anfragen.
 *
 * Der Server drosselt auf eine Anfrage je Viertelsekunde. Etwas mehr, damit die
 * Drosselung nicht wegen einer Millisekunde zuschlägt: dreissig Etappen dauern
 * damit knapp zehn Sekunden, und sie laufen im Hintergrund.
 */
export const WEATHER_GAP_MS = 300

/**
 * Wartezeit vor dem zweiten Versuch, wenn der Server die Drosselung ziehen
 * musste.
 *
 * Genau ein zweiter Versuch und keine Kette - dieselbe Überlegung wie beim
 * Routendienst: eine Wiederholungsschleife machte bei einem Dienst, der weg
 * ist, aus dreissig Anfragen sechzig.
 */
export const WEATHER_RETRY_MS = 1000

/** Eine Etappe, für die etwas zu holen ist. */
export interface WeatherJob {
  stageId: string
  /** Beschriftung für die Fortschrittsanzeige. */
  label: string
  /** Die Punkte der Aufzeichnung, mit denen gefragt wird. */
  samples: TrackFix[]
  /** Zeitbereich der Fahrt, für den geholt wird. */
  startedAt: number
  endedAt: number
}

/** Wie weit der Lauf ist. */
export interface WeatherProgress {
  total: number
  done: number
  failed: number
  /** Beschriftung der Etappe, die gerade läuft. */
  current: string
}

/** Wie der Lauf ausgegangen ist. */
export interface WeatherRunResult {
  done: number
  failed: number
  /** Der Grund, an dem der Lauf hängengeblieben ist, sonst null. */
  error: string | null
  aborted: boolean
}

/** Was der Lauf von aussen braucht. Alles einsetzbar, damit er prüfbar bleibt. */
export interface WeatherRunDeps {
  fetchWeather: (samples: TrackFix[], signal?: AbortSignal) => Promise<WeatherResponse>
  /** Legt das Ergebnis im Tagebucheintrag ab. */
  save: (stageId: string, weather: DiaryWeather) => void
  onProgress?: (progress: WeatherProgress) => void
  gapMs?: number
  retryMs?: number
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
  now?: () => number
  signal?: AbortSignal
}

/**
 * Die Etappen, für die es etwas zu holen gibt.
 *
 * Ausschliesslich solche mit Aufzeichnung, und das ist die Regel dieser
 * Funktion: ohne Track keine Punkte, ohne Punkte keine Anfrage. Eine Etappe
 * ohne GPX bekommt keinen Vorschlag - auch keinen schwächeren aus Zielkoordinate
 * und geratener Ankunftszeit.
 *
 * Ausgelassene Etappen bleiben liegen: sie wurden nicht gefahren. Eine Etappe,
 * für die schon etwas Passendes dasteht, ebenfalls - „gefragt und Werte
 * bekommen“ ist erledigt, und es jeden Morgen erneut einzuholen wäre der
 * Dauerbetrieb, den der Zwischenspeicher verhindern soll.
 *
 * Der Sonderfall ist die leere Antwort auf einen Tag, der beim Fragen noch zu
 * frisch war (Zustand 'pending'). Genau so läuft der beworbene Regelfall: GPX
 * abends auf dem Platz eingelesen, Netz da, gefragt - und das Archiv des
 * Dienstes hat für heute noch nichts. Das ist keine Auskunft, sondern ein „noch
 * nicht“, und es wäre falsch, sie für die ganze Reise als erledigt zu führen.
 * Solche Etappen kommen wieder dran, aber höchstens einmal je
 * WEATHER_RECHECK_AGE_MS - genau der Takt, in dem der Server seinen eigenen
 * Eintrag erneuert. Häufiger zu fragen brächte dieselbe Antwort aus seinem
 * Zwischenspeicher zurück und schriebe sie mit neuem Zeitstempel in den
 * Bestand.
 *
 * Angestossen wird der Nachlauf, wenn sich der Bestand ändert oder die App
 * geöffnet wird (weatherApi.ts). Für einen Tag, der nachgeholt werden muss,
 * reicht das: dazwischen liegt eine Nacht.
 *
 * @param jetzt Bezugspunkt für den Takt der Nachfrage.
 */
export function stagesNeedingWeather(
  stages: Stage[],
  diary: Record<string, DiaryEntry>,
  jetzt = Date.now(),
): WeatherJob[] {
  const jobs: WeatherJob[] = []
  for (const stage of stages) {
    if (stage.skip) continue
    const entry = diary[stage.id]
    const zustand = weatherState(entry, jetzt)
    if (zustand === 'pending') {
      const wetter = entry?.weatherFromTrack
      if (!wetter || !weatherRecheckDue(wetter, jetzt)) continue
    } else if (zustand !== 'none' && zustand !== 'stale') continue
    const job = weatherJob(stage, entry)
    if (job) jobs.push(job)
  }
  return jobs
}

/**
 * Der Auftrag zu einer Etappe, oder nichts.
 *
 * Nichts, sobald irgendetwas an der Aufzeichnung fehlt - die Punkte, der
 * Anfang, das Ende. Geprüft wird das hier und nicht in der Oberfläche: eine
 * Anfrage ohne Grundlage soll gar nicht erst zusammengebaut werden können.
 */
export function weatherJob(stage: Stage, entry: DiaryEntry | undefined): WeatherJob | null {
  const track = entry?.track
  if (!track || track.startedAt === null || track.endedAt === null) return null
  const samples = weatherSamples(track)
  if (samples.length === 0) return null
  return {
    stageId: stage.id,
    label: `${stage.from} → ${stage.to}`,
    samples,
    startedAt: track.startedAt,
    endedAt: track.endedAt,
  }
}

/**
 * Sagt, ob ein Fehler den ganzen Lauf beendet.
 *
 * Kein Netz, keine gültige Anmeldung oder ein Dienst, der nicht antwortet:
 * dann scheitern die folgenden Anfragen genauso. Kein Netz ist dabei kein
 * Fehlschlag, sondern das Ende des Laufs - der nächste beginnt, sobald der
 * Browser wieder online meldet.
 */
export function isFatal(error: unknown): boolean {
  if (error instanceof OfflineError) return true
  if (error instanceof ApiError) {
    return error.isAuthProblem || error.code === 'upstream_unavailable'
  }
  return false
}

/**
 * Der Fehler in einem Satz, den man dem Nutzer zeigen kann.
 *
 * Die Codes stammen aus der Zusage des Servers. Was dort nicht aufgeführt ist,
 * bekommt eine allgemeine Meldung samt Statuscode - erfundene Deutungen sind
 * schlimmer als ein zugegebenes „unbekannt“.
 */
export function weatherErrorText(error: unknown): string {
  if (error instanceof OfflineError) {
    return 'Kein Netz. Das Wetter wird geholt, sobald wieder eine Verbindung da ist.'
  }
  if (error instanceof ApiError) {
    if (error.isAuthProblem) return 'Die Anmeldung ist abgelaufen. Neu anmelden und noch einmal.'
    if (error.code === 'upstream_unavailable') {
      return 'Der Wetterdienst antwortet nicht. Später noch einmal versuchen.'
    }
    if (error.code === 'bad_request') {
      return 'Mit den Punkten dieser Aufzeichnung kann der Dienst nichts anfangen.'
    }
    return `Der Server antwortete mit ${String(error.status)} (${error.code}).`
  }
  return 'Das Wetter liess sich nicht holen.'
}

/**
 * Macht aus der Antwort des Servers das, was im Tagebuch liegen bleibt.
 *
 * Eine leere Stundenliste ist ausdrücklich ein gültiges Ergebnis und kein
 * Fehler: es gibt Tage und Ecken, für die das Archiv nichts hat. Gespeichert
 * wird sie trotzdem, damit dieselbe Etappe nicht bei jedem Start wieder fragt -
 * und im Tagebuch steht dann eben nichts, statt einer Zahl, die niemand
 * gemessen hat.
 *
 * Mit fetchedAt geht die Zeit des Abrufs mit, und erst sie macht die leere
 * Antwort lesbar: zusammen mit endedAt sagt sie, ob der Dienst den Tag
 * überhaupt schon fertig hatte (weatherSettled in lib/weather.ts).
 */
export function weatherFromAnswer(
  answer: WeatherResponse,
  job: WeatherJob,
  at: number,
): DiaryWeather {
  return {
    hours: weatherHoursFrom(answer.hours),
    fetchedAt: at,
    source: typeof answer.provider === 'string' ? answer.provider : '',
    attribution:
      typeof answer.attribution === 'string' && answer.attribution !== ''
        ? answer.attribution
        : WEATHER_ATTRIBUTION,
    startedAt: job.startedAt,
    endedAt: job.endedAt,
  }
}

/**
 * Holt das Wetter der übergebenen Etappen, eine nach der anderen.
 *
 * Der Reihe nach und nicht parallel: der Server drosselt, und dreissig
 * gleichzeitige Anfragen bekämen neunundzwanzig Abweisungen. Gewartet wird vor
 * der Anfrage und nicht danach - so geht die erste sofort los, und der Abstand
 * gilt trotzdem für jedes Paar.
 *
 * Gespeichert wird nach jeder einzelnen Etappe. Reisst die Verbindung mitten im
 * Lauf ab, ist alles behalten, was schon geholt wurde, und der nächste Lauf
 * nimmt genau den Rest.
 */
export async function fetchWeatherForStages(
  jobs: WeatherJob[],
  deps: WeatherRunDeps,
): Promise<WeatherRunResult> {
  const gap = deps.gapMs ?? WEATHER_GAP_MS
  const sleep = deps.sleep ?? pause
  const now = deps.now ?? Date.now
  const total = jobs.length

  let done = 0
  let failed = 0

  const melden = (current: string) => {
    deps.onProgress?.({ total, done, failed, current })
  }

  for (const [index, job] of jobs.entries()) {
    if (deps.signal?.aborted) return { done, failed, error: null, aborted: true }
    melden(job.label)

    if (index > 0) {
      await sleep(gap, deps.signal)
      if (deps.signal?.aborted) return { done, failed, error: null, aborted: true }
    }

    try {
      const answer = await holeWetter(job, deps, sleep)
      deps.save(job.stageId, weatherFromAnswer(answer, job, now()))
      done++
    } catch (error) {
      // Ein Abbruch lässt fetch werfen. Das ist kein Fehler, sondern der
      // Wunsch des Aufrufers, und darf nicht als Fehlschlag gezählt werden.
      if (deps.signal?.aborted) return { done, failed, error: null, aborted: true }
      if (isFatal(error)) {
        return { done, failed, error: weatherErrorText(error), aborted: false }
      }
      failed++
    }
  }

  melden('')
  return { done, failed, error: null, aborted: false }
}

/** Holt das Wetter einer Etappe, mit einem zweiten Versuch nach einer Absage. */
async function holeWetter(
  job: WeatherJob,
  deps: WeatherRunDeps,
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>,
): Promise<WeatherResponse> {
  try {
    return await deps.fetchWeather(job.samples, deps.signal)
  } catch (error) {
    if (deps.signal?.aborted || !isThrottled(error)) throw error
    await sleep(deps.retryMs ?? WEATHER_RETRY_MS, deps.signal)
    if (deps.signal?.aborted) throw error
    return deps.fetchWeather(job.samples, deps.signal)
  }
}

/** Die Absage, hinter der eine Drosselung stecken kann. */
function isThrottled(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503 && error.code === 'upstream_unavailable'
}

/**
 * Wartet, lässt sich aber abbrechen.
 *
 * Aufgelöst wird auch beim Abbruch, denn der Aufrufer prüft das Abbruchzeichen
 * ohnehin selbst - eine Ablehnung wäre hier nur eine Ausnahme, die niemand
 * behandeln will. Dieselbe Hilfsfunktion wie in stageRouting.ts; sie steht
 * zweimal da, weil ein gemeinsames Modul für sechs Zeilen mehr Abhängigkeit
 * schafft, als es spart.
 */
function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abbrechen)
      resolve()
    }, ms)
    function abbrechen() {
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', abbrechen, { once: true })
  })
}
